import { concat } from '@ember/helper';
import { hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { task } from 'ember-concurrency';
import { tracked } from '@glimmer/tracking';
import { uniqueId } from '@ember/helper';
import Component from '@glimmer/component';
import FileDropzone from 'ember-file-upload/components/file-dropzone';
import fileQueue from 'ember-file-upload/helpers/file-queue';
import type { UploadFile, Queue } from 'ember-file-upload';
import { PhCloudArrowUp } from 'ember-phosphor-icons';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { buildTioAuthHeaders } from 'tio-data/utils/auth-headers';
import type FeaturesService from '../services/features.ts';

export interface FileUploaderSignature<T> {
  Args: {
    disabled?: boolean;
    isPublic?: boolean;
    // Is public image removes the need for a signed URL
    isPublicImage?: boolean;
    accept?: string;
    didUploadFile?: (content: T) => void;
    useDropzone?: boolean;
  };
  Blocks: {
    default: [
      {
        queue: Queue;
        uniqueId: string;
        uploadTask: { isRunning: boolean };
      },
    ];
  };
  Element: HTMLDivElement;
}

export default class FileUploaderComponent<T> extends Component<FileUploaderSignature<T>> {
  @service declare features: FeaturesService;
  @tracked isFileDialogOpen = false;

  get isPublic() {
    return !!this.args.isPublic;
  }

  // WarpDrive migration Phase 2.4: buildBaseURL replaces adapter.urlForCreateRecord.
  get collectionPath() {
    const resourcePath = this.isPublic ? 'public-assets' : 'digital-assets';
    const opts: QueryUrlOptions = {
      identifier: { type: resourcePath },
      op: 'query',
      resourcePath,
    };
    return buildBaseURL(opts);
  }

  // WarpDrive migration Phase 2.4: buildTioAuthHeaders replaces adapter.headers
  // for direct fetch calls (presigned-S3 upload path).
  get headers() {
    return buildTioAuthHeaders({
      apiKey: this.features.ENV?.apiKey ?? '',
      contentType: 'application/vnd.api+json',
    });
  }

  newUploadAsset = (queue: UploadFile) => {
    this.uploadAsset.perform(queue);
    this.isFileDialogOpen = false;
  };

  uploadAsset = task({ maxConcurrency: 3, enqueue: true }, async (queue: UploadFile) => {
    const apiResponse = await fetch(`${this.collectionPath}/s3-signed-url`, {
      headers: this.headers,
      method: 'POST',
      body: JSON.stringify({ filename: queue.file.name, public_image: this.args.isPublicImage }),
    });
    const content = await apiResponse.json();

    await queue.uploadBinary(content.url, {
      method: 'PUT',
    });
    await this.args.didUploadFile?.(content);
  });

  openFileDialog = (event: Event) => {
    const fileInput = event.target as HTMLInputElement;
    this.isFileDialogOpen = true;

    fileInput!.addEventListener(
      'cancel',
      () => {
        this.fileSelectedCanceled();
      },
      { once: true }
    );
  };

  fileSelectedCanceled = () => {
    this.isFileDialogOpen = false;
  };

  <template>
    <div ...attributes>
      {{#let (uniqueId) as |uniqueId|}}
        {{#let
          (fileQueue name=(concat "upload-" uniqueId) onFileAdded=this.newUploadAsset)
          as |queue|
        }}
          {{#if (has-block)}}
            {{yield (hash queue=queue uniqueId=uniqueId uploadTask=this.uploadAsset)}}
          {{else if @useDropzone}}
            {{#unless @disabled}}
              <FileDropzone
                class="tio-place-center h-full"
                @queue={{queue}}
                @onDrop={{this.fileSelectedCanceled}}
                as |dropzone|
              >
                <div class="w-full">
                  {{#if this.isFileDialogOpen}}
                    <div id="drag-window" class="fixed inset-0 z-40 tio-bg-sunken opacity-50"></div>
                  {{/if}}
                  <div class="tio-row-center w-full">
                    <label
                      for={{uniqueId}}
                      class="tio-place-center w-full h-48 border-2 tio-border-default border-dashed cursor-pointer tio-surface-sunken hover:tio-bg-sunken-hover"
                      {{on "click" this.openFileDialog}}
                    >
                      {{#if dropzone.active}}
                        {{t "file_upload.drop_to_upload"}}
                      {{else if queue.files.length}}
                        {{t "file_upload.uploading"}}
                        {{queue.files.length}}
                        {{t "file_upload.files"}}
                        ({{queue.progress}}%)
                      {{else if dropzone.supported}}
                        <div class="tio-place-center pt-5 pb-6">
                          <PhCloudArrowUp @size="32" class="mb-2" />
                          <p class="mb-2 text-sm tio-fg-muted"><span class="font-semibold">{{t
                                "file_upload.select_a_file"
                              }}</span>
                            {{t "file_upload.or_drag_and_drop"}}</p>
                          {{! <p class="tio-text-caption">PDF, DOC, PNG, JPG
                              (Max file size 5MB)</p> }}
                        </div>
                      {{/if}}
                      <input
                        id={{uniqueId}}
                        type="file"
                        {{queue.selectFile}}
                        accept={{@accept}}
                        class="hidden"
                        multiple="true"
                        {{on "click" this.openFileDialog}}
                        {{on "change" this.fileSelectedCanceled}}
                      />
                    </label>
                  </div>
                </div>
              </FileDropzone>
            {{/unless}}
          {{else}}
            <input
              id={{uniqueId}}
              type="file"
              {{queue.selectFile}}
              accept={{@accept}}
              class=""
              multiple="true"
            />
          {{/if}}
        {{/let}}
      {{/let}}
    </div>
  </template>
}
